import { call, fork, put, all, select, getContext, take, takeEvery } from 'redux-saga/effects';
import { Route, State as RouteState } from 'config/routes';
import { setAuthModal, setToastMessage } from 'store/state/app/actions';
import { AuthenticationModalType } from 'components/authentication/types';
import { queryData } from 'store/sagas/apiService';
import { ISaveSearch, LoadOptions, LoadType } from 'store/sagas/apiService/types';
import { SortDirection, SortField } from 'components/listing-sort/types';
import { verifyRessetPasswordToken, fetchArticles, waitForUserResolve } from 'store/sagas/routing/handlers/utils';
import { navigateTo } from 'store/state/router/actions';
import { insideReactNative } from 'react-native/lib/platform';
import { lastSearchHistorySelector } from 'store/state/domainData/selectors';
import { Amenity } from 'components/filters/types';
import { searchBboxSelector } from 'store/state/selectors/search';
import { boundsToTileRange } from 'components/map/utils';
import { MAX_ZOOM, prepareSecureRoomType } from '../search/utils';
import { UserTokenStore } from 'helpers/userToken';
import { checkUserSearchHistoryUpdatedPattern } from 'store/sagas/userWatcher';
import { resetDomainData } from 'store/state/domainData/actions';
import { LOGOUT } from 'store/state/app/types';
import { userLoginPattern } from 'store/sagas/fetchFavoritesWatcher';


function* clearUserSearchHistory() {
  yield put(resetDomainData({ loadType: LoadType.CurrentUserSearchHistory }));
}

function* loadSearchHistory(userToken: string) {
  yield call(clearUserSearchHistory);

  yield fork(queryData, { loadType: LoadType.CurrentUserSearchHistory, meta: { variables: { userToken } } });
  yield take(checkUserSearchHistoryUpdatedPattern);

  const lastSearch: ISaveSearch = yield select(lastSearchHistorySelector);

  if (lastSearch) {
    const {
      areaRange,
      bathsRange,
      buildingClass,
      floorRange,
      generalCondition,
      monthlyTaxRange,
      ppmRange,
      priceRange,
      roomsRange,
      sellerType,
    } = lastSearch.query;

    const amenities = lastSearch.query.amenities ? (Object.keys(lastSearch.query.amenities) as Amenity[]).reduce<Partial<Record<Amenity, boolean>>>((acc, v) => {
      if (lastSearch.query.amenities[v] && (v as string) !== '__typename') {
        acc[v] = true;
      }
      return acc;
    }, {}) : {};

    const { restAmenities, secureRoomType } = prepareSecureRoomType(amenities);

    const docIds = (!lastSearch.locationDocIds && !lastSearch.term) ? undefined : (lastSearch.locationDocIds || [ lastSearch.term ]);

    if (docIds) {
      yield call(queryData, {
        loadType: LoadType.SearchDoc,
        meta: {
          variables: { docIds },
          errorPolicy: 'none',
        },
      });
    }

    const bb = yield select(searchBboxSelector);

    const loadOptions: LoadOptions<LoadType.HomepageLastSearchListings> = {
      loadType: LoadType.HomepageLastSearchListings,
      meta: {
        variables: {
          sort: !docIds ? undefined : [ {
            docIds,
            field: SortField.Geometry,
            order: SortDirection.Asc,
            reference: null,
          } ],
          dealType: lastSearch.query.dealType,
          tileRanges: [ boundsToTileRange(bb, MAX_ZOOM) ],
          limit: 50,
          offset: 0,
          poiTypes: [ 'bulletin' ],
          searchContext: 'marketplace',
          priceRange,
          ppmRange,
          monthlyTaxRange,
          roomsRange,
          amenities: restAmenities,
          bathsRange,
          buildingClass,
          sellerType,
          generalCondition,
          floorRange,
          areaRange,
          secureRoomType,
        },
      },
    };
    yield call(queryData, loadOptions);
  }
}

function* loadLastSearchHistoryListings() {
  const logger = yield getContext('logger');
  try {
    yield call(waitForUserResolve);
    const tokenStore: UserTokenStore = yield getContext('tokenStore');
    const userToken = tokenStore.get();
    if (!userToken) return;
    yield call(loadSearchHistory, userToken);
  }
  catch (e) {
    const errorMessage = e instanceof Error ? e.message : JSON.stringify(e);
    const message = `Error in loadLastSearchHistoryListings: ${errorMessage}`;
    // tslint:disable-next-line: no-console
    logger.error(message, e);
  }
}


export function* loadData(initialState: RouteState) {
  // TODO: move it to qgl file after BE will fix Reference type
  const sortFields: any = [
    [
      { field: SortField.PriceEstimation, order:  SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'רחובות-ישראל', field: 'doc-id' },
        docId: 'רחובות-ישראל',
      },
    ],
    [
      { field: SortField.PriceEstimation, order:  SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'רמת-גן-ישראל', field: 'doc-id' },
        docId: 'רמת-גן-ישראל',
      },
    ],
    [
      { field: SortField.PriceEstimation, order:  SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'נתניה-ישראל', field: 'doc-id' },
        docId: 'נתניה-ישראל',
      },
    ],
    [
      { field: SortField.BestSchool, order:  SortDirection.Asc },
      { field: SortField.FamilyFriendly, order: SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'רחובות-ישראל', field: 'doc-id' },
        docId: 'רחובות-ישראל',
      },
    ],
    [
      { field: SortField.BestSchool, order:  SortDirection.Asc },
      { field: SortField.FamilyFriendly, order: SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'תל-אביב-יפו-ישראל', field: 'doc-id' },
        docId: 'תל-אביב-יפו-ישראל',
      },
    ],
    [
      { field: SortField.BestSchool, order:  SortDirection.Asc },
      { field: SortField.FamilyFriendly, order: SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'חיפה-ישראל', field: 'doc-id' },
        docId: 'חיפה-ישראל',
      },
    ],
    [
      { field: SortField.QuietStreets, order:  SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'באר-שבע-ישראל', field: 'doc-id' },
        docId: 'באר-שבע-ישראל',
      },
    ],
    [
      { field: SortField.QuietStreets, order:  SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'תל-אביב-יפו-ישראל', field: 'doc-id' },
        docId: 'תל-אביב-יפו-ישראל',
      },
    ],
    [
      { field: SortField.QuietStreets, order:  SortDirection.Asc },
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'קרית-מוצקין-ישראל', field: 'doc-id' },
        docId: 'קרית-מוצקין-ישראל',
      },
    ],
    [
      { field: SortField.Geometry, order: SortDirection.Asc,
        reference: { dataset: { country: 'israel', type: 'places' }, value: 'תל-אביב-יפו-ישראל', field: 'doc-id' },
        docId: 'תל-אביב-יפו-ישראל',
      },
    ],
  ];
  yield fork(queryData, {
    loadType: LoadType.IlHomePageBulletins,
    meta: {
      variables: {
        sort1: sortFields[0],
        sort2: sortFields[1],
        sort3: sortFields[2],
        sort4: sortFields[3],
        sort5: sortFields[4],
        sort6: sortFields[5],
        sort7: sortFields[6],
        sort8: sortFields[7],
        sort9: sortFields[8],
        sort10: sortFields[9],
      },
    },
  });
  const loadOptions: LoadOptions<LoadType.AreasWithCommercialInfoPage> = {
    loadType: LoadType.AreasWithCommercialInfoPage,
    meta: {
      variables: {},
    },
  };
  yield fork(queryData, loadOptions);
}

function* homeBaseHandler(initialState: RouteState, { withLastSearch }: { withLastSearch: boolean }) {
  const isMobileApp = insideReactNative();
  yield all([
    call(loadData, initialState),
    call(queryData, {
      loadType: LoadType.HomePageBanner,
      meta: { variables: { useTimestamp: isMobileApp } },
    }),
    call(fetchArticles, null, 'homepage'),
    ...(withLastSearch ? [ call(loadLastSearchHistoryListings) ] : []),
  ]);

  const token = initialState.params.token;

  if (token) {
    const isTokenValid: boolean = yield call(verifyRessetPasswordToken, token);

    if (isTokenValid) {
      yield put(setAuthModal({ type: AuthenticationModalType.SetPassword }, { isUserInitiated: false }));
    }
    else {
      yield put(setAuthModal({ type: AuthenticationModalType.ResetPassword }, { isUserInitiated: false }));
      yield put(setToastMessage({ term: 'resetPasswordToken.expiredOrInvalid' }));
      yield put(navigateTo(Route.Home, null, { replace: true }));
    }
  }
}

export function* homeHandler(initialState: RouteState) {
  yield call(homeBaseHandler, initialState, { withLastSearch: true });
  yield takeEvery(LOGOUT, clearUserSearchHistory);
  yield takeEvery(userLoginPattern, loadLastSearchHistoryListings);
}

export function* homeSSRHandler(initialState: RouteState) {
  yield call(homeBaseHandler, initialState, { withLastSearch: false });
}
